<script setup>
import { LogoutIcon } from '@heroicons/vue/outline'

import { useAuth } from '@/Hooks/Company/useAuth'
import { usePermissions } from '@/Hooks/Company/usePermissions'

import usePrimaryNavigation from '@/Hooks/Company/Navigation/usePrimaryNavigation'

import CompanyDropdown from '@/Components/Company/Header/CompanyDropdown.vue'

import { TextButton, Typography } from '@aryeo/runway'

import SidebarItem from '@/Components/Company/Sidebar/SidebarItem.vue'
import InertiaButton from '@/Components/Shared/Buttons/InertiaButton.vue'
import CsrfToken from '@/Components/Shared/CsrfToken.vue'
import SidebarSection from '@/Components/Shared/Navigation/Sidebar/SidebarSection.vue'
import CreateListingButton from '@/Components/Shared/Listings/Create/CreateListingButton.vue'

import AssignMediaRequestAlert from '@/Components/Company/MediaRequests/AssignMediaRequestAlert.vue'
import SimpleAlert from '@/Runway/Alerts/Simple.vue'

const {
  authOrganization,
  integrationRequiringAuthorization,
  integrationRequiringConfigurationUpdate,
  integrationExpiringSoon,
  needsHomeBase,
  otherTeamMemberNeedsHomeBase,
  portalAppConfigRequiringAction,
  organizationNeedsBillingTaxDetails,
} = useAuth()
const { hasPermission } = usePermissions()
</script>

<template>
  <div class="pt-6 pb-12 space-y-6">
    <SimpleAlert
      v-if="hasPermission('settings_manage') && authOrganization.requires_billing_update"
      class="mx-6"
      type="danger"
      title="Billing Update Required"
      :hide-icon="true"
      description="Your subscription has outstanding invoices and your feature access may be limited soon."
    >
      <TextButton text="Update Now" :link="route('web.company.billing-settings.show')" isSmall />
    </SimpleAlert>
    <SimpleAlert
      v-if="
        hasPermission('settings_manage') &&
        organizationNeedsBillingTaxDetails &&
        authOrganization.is_pro_account
      "
      class="mx-6"
      type="danger"
      title="Billing Update Required"
      :hide-icon="true"
      description="Some of your required billing information is missing from your account."
    >
      <TextButton text="Update Now" :link="route('web.company.billing-settings.show')" isSmall />
    </SimpleAlert>
    <SimpleAlert
      v-if="hasPermission('app_store_view_any') && integrationRequiringAuthorization"
      class="mx-6"
      type="warning"
      title="Integration Update Required"
      :hide-icon="true"
      description="You have an integration that requires reauthorization. Jump over to your App Store to complete this update."
    >
      <TextButton text="Update Now" :link="route('web.company.app-store.show')" isSmall />
    </SimpleAlert>
    <SimpleAlert
      v-if="hasPermission('app_store_view_any') && integrationRequiringConfigurationUpdate"
      class="mx-6"
      type="warning"
      title="Integration Update Required"
      :hide-icon="true"
      description="Your QuickBooks tax rates need updating."
    >
      <TextButton
        text="Update Now"
        :link="route('web.company.quickbooks.tax-rates.index')"
        isSmall
      />
    </SimpleAlert>
    <SimpleAlert
      v-else-if="hasPermission('app_store_view_any') && integrationExpiringSoon"
      class="mx-6"
      type="warning"
      title="Integration Attention Needed"
      :hide-icon="true"
      description="You have an integration that will expire soon. Jump over to your App Store to complete this update."
    >
      <TextButton text="Update Now" :link="route('web.company.app-store.show')" isSmall />
    </SimpleAlert>
    <SimpleAlert
      v-if="hasPermission('settings_manage') && portalAppConfigRequiringAction"
      class="mx-6"
      type="danger"
      title="Mobile App Action Required"
      :hide-icon="true"
      description="Your mobile app will not receive updates until you take action."
    >
      <TextButton
        text="View Now"
        :link="route('web.company.customer-portal.app.dashboard.show')"
        isSmall
      />
    </SimpleAlert>
    <SimpleAlert
      v-if="hasPermission('settings_manage') && otherTeamMemberNeedsHomeBase"
      class="mx-6"
      type="info"
      title="Team Member Missing Home Base"
      :hide-icon="true"
      description="At least one of your team members is configured to use either dynamic drive times or travel fees, but does not have a home base set, yet."
    >
      <TextButton
        text="View team members"
        :link="route('web.company.company-team-members.index')"
        isSmall
      />
    </SimpleAlert>
    <SimpleAlert
      v-if="needsHomeBase"
      class="mx-6"
      type="info"
      title="Missing Home Base"
      :hide-icon="true"
      description="Your account is configured to use dynamic drive times and/or travel fees, but you do not have a home base set, yet."
    >
      <TextButton text="Set Home Base" :link="route('web.company.home-bases.index')" isSmall />
    </SimpleAlert>

    <SimpleAlert
      v-if="!authOrganization.is_pro_account"
      class="mx-6"
      type="info"
      title="You are using Aryeo Lite"
      :hide-icon="true"
      description="Unlock powerful features to help manage your business with an Aryeo Pro Account."
    >
      <TextButton :link="route('web.company.billing.upgrade.show')" text="Learn more &rarr;" />
    </SimpleAlert>
    <AssignMediaRequestAlert inSidebar />
    <div
      class="flex flex-col mx-6 space-y-3"
      v-if="hasPermission('orders_manage') || hasPermission('listing_create')"
    >
      <CreateListingButton :iconName="null" />
      <InertiaButton
        v-if="!authOrganization.is_pro_account"
        :link="route('web.company.billing.upgrade.show')"
        color="neutral"
        isSecondary
        text="Upgrade Account"
      />

      <div class="border-b border-border-light pt-3" />
    </div>

    <div class="space-y-6">
      <SidebarSection
        v-for="section in usePrimaryNavigation().sections.filter((section) => section.show)"
        :key="section.name"
        :name="section.name"
      >
        <SidebarItem
          v-for="item in section.children.filter((item) => item.show && item.targetRouteName)"
          :key="item.name"
          :name="item.name"
          :disabled="item.disabled"
          :image="item.image"
          :icon-component="item.icon"
          :target-route-name="item.targetRouteName"
          :target-route-attributes="item.targetRouteAttributes"
          :is-inertia-link="item.routeIsInertia"
          :route-names-to-check-if-current="item.routeNamesToCheckIfCurrent"
          :ghost="item.ghost || false"
          :badge="item.badge"
          item-of-primary-column
        />
      </SidebarSection>
    </div>

    <div class="flex-col space-y-6 md:hidden">
      <hr class="h-1 mx-6" />

      <SidebarSection name="Current Group" :use-negative-margin="false">
        <CompanyDropdown :dark-mode="false" />
      </SidebarSection>

      <hr class="h-1 mx-6" />

      <SidebarSection :use-negative-margin="false">
        <form method="post" :action="route('web.company.login.destroy')" class="h-full">
          <CsrfToken />
          <button
            type="submit"
            class="flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:text-gray-900 hover:bg-gray-50 group"
          >
            <LogoutIcon
              class="w-5 h-5 mr-3 text-gray-500 shrink-0 group-hover:text-gray-600"
              aria-hidden="true"
            />
            Logout
          </button>
        </form>
      </SidebarSection>
    </div>
  </div>
</template>
